import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { Panel, PanelHead } from '../../../music24/components/primitives/Panel';
import { songsBrewMessages as messages } from '../../../music24/artistHome/songsBrew/songsBrewMessages';
import type { SettingsSaveState } from '../SettingsSaveStatus';
import { SongsBrewSwitchRow } from './SongsBrewSwitchRow';
import { useSongsBrewSetting } from './useSongsBrewSetting';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the row and its skeleton */
import styles from './SongsBrewSwitchRow.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface Props {
  setSaveState: (state: SettingsSaveState) => void;
}

/**
 * The Newsletter group, last in Settings → Notifications (design: songsbrew-promotion c1). It reads
 * and writes the SongsBrew platform through the `newsletter` router, not
 * `users.updateNotificationPreferences`. While the state loads it shows one skeleton row; when the
 * call fails, the server is old or the platform is not set up, the group is not drawn.
 */
const SongsBrewNotificationGroupComponent: React.FC<Props> = ({ setSaveState }) => {
  const intl = useIntl();
  const setting = useSongsBrewSetting(setSaveState);

  if (!setting.isLoading && setting.status === null) {
    return null;
  }

  return (
    <Panel as="section" data-testid="settings-songsbrew">
      <PanelHead title={intl.formatMessage(messages.settingsGroup)} subtitle={intl.formatMessage(messages.byline)} />
      {setting.status === null ? (
        <div className={`${styles.skeleton} placeholder-glow`} aria-hidden="true">
          <span className={`placeholder ${styles.skeletonMark}`} />
          <span className={`placeholder ${styles.skeletonText}`} />
        </div>
      ) : (
        <SongsBrewSwitchRow
          status={setting.status}
          sendTrackUrl={setting.sendTrackUrl}
          needsIssueLink={setting.needsIssueLink}
          onChange={setting.toggle}
        />
      )}
    </Panel>
  );
};

export const SongsBrewNotificationGroup = TypedMemo(SongsBrewNotificationGroupComponent);
